import type { DongTangTruong } from '@qlk/shared';
import { SO_NGAY_NUA_TANG_TRUONG, ngayGonVn } from '@qlk/shared';

import { soNguyen } from '../../../ui/index.js';
import { duongKe, toaDo, type KhungBieuDo } from './bieuDo.js';

/**
 * Phần tính toán của khung "Tăng trưởng lượt xem · 7 ngày".
 *
 * Tách khỏi component để test bằng Vitest ở môi trường `node`: hai thứ dễ sai
 * ở đây là **thang dùng chung** của hai nửa đường và câu `aria-label`, cả hai
 * đều kiểm được bằng chuỗi.
 */

/**
 * Khung của đường 14 ngày — nhỏ, nằm trong một dòng của danh sách.
 *
 * `dem` 4 trên 36 vì nét vẽ dày 2px *không co theo khung*
 * (`vectorEffect="non-scaling-stroke"`), nên đỉnh cao nhất phải dừng cách mép.
 */
export const KHUNG_DUONG_14: KhungBieuDo = { rong: 120, cao: 36, dem: 4 };

/** Màu ô chữ cái đầu theo hạng, token ở `theme.css`. */
export const MAU_HANG = [
  'var(--color-hang-1)',
  'var(--color-hang-2)',
  'var(--color-hang-3)',
  'var(--color-hang-4)',
  'var(--color-hang-5)',
] as const;

/**
 * Chữ của ô chữ cái đầu: trắng trên ba hạng đầu, chàm đậm trên hai hạng nhạt.
 *
 * Hạng 4–5 là `#A5B4FC` / `#C7D2FE`; chữ trắng trên đó chỉ đạt ~1,8:1, dưới cả
 * ngưỡng 3:1 của chữ lớn.
 */
export function mauChuHang(hang: number): string {
  return hang <= 3 ? '#FFFFFF' : 'var(--color-doan-giu-lai)';
}

export interface Duong14 {
  /** Nét xám: 7 ngày của tuần trước. */
  truoc: string;
  /** Nét màu: 7 ngày qua. */
  nay: string;
}

/**
 * Hai nét của đường 14 ngày, **một thang chung**.
 *
 * `toaDo` nhận cả 14 giá trị rồi mới cắt: tính thang riêng cho từng nửa thì
 * hai nửa cùng cao bằng nhau bất kể số thật, và cả hình nói ngược lại con số
 * ngay cạnh nó.
 *
 * Nét xám lấy **8** điểm (0–7) chứ không 7: đoạn nối ngày cuối tuần trước với
 * ngày đầu tuần này phải có người vẽ, không thì đường đứt một khúc giữa hình
 * và đọc ra là "mất dữ liệu". Nét màu vẽ sau nên nó đè lên đoạn nối đó.
 */
export function duong14(chuoi14: number[]): Duong14 {
  const diem = toaDo(chuoi14, KHUNG_DUONG_14);

  return {
    truoc: duongKe(diem.slice(0, SO_NGAY_NUA_TANG_TRUONG + 1)),
    nay: duongKe(diem.slice(SO_NGAY_NUA_TANG_TRUONG)),
  };
}

/**
 * `aria-label` của đường: trình đọc màn hình không đọc được một nét SVG, nên
 * câu này phải tự chứa đủ thông tin của hình.
 */
export function nhanDuong14(d: DongTangTruong): string {
  return `Lượt xem 14 ngày của ${d.ten}: tuần trước ${soNguyen(d.luotTruoc)}, 7 ngày qua ${soNguyen(d.luot7)}`;
}

/** `+120.400 lượt xem` / `−98.000 lượt xem` — dấu nói chiều, không dùng mũi tên. */
export function moTaDelta(delta: number): string {
  const dau = delta > 0 ? '+' : delta < 0 ? '−' : '';
  return `${dau}${soNguyen(Math.abs(delta))} lượt xem`;
}

/** `19/10 – 25/10` cho chân khung. */
export function khoangGon(tu: string, den: string): string {
  if (tu === '' || den === '') return '';
  return `${ngayGonVn(tu)} – ${ngayGonVn(den)}`;
}
